import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { env } from '../../utils/env';
import { setCredentials, logout } from '../slices';
import { refreshSession, type AuthResult } from '../../services/cognito';
import {
  credentialsFromRefresh,
  needsProactiveRefresh,
  shouldAttemptReauth,
} from './reauthHelpers';

const REQUEST_TIMEOUT_MS = 60_000;

/** Shared in-flight refresh so concurrent RTK queries do not race Cognito. */
let refreshPromise: Promise<AuthResult | null> | null = null;

function runSharedRefresh(): Promise<AuthResult | null> {
  if (!refreshPromise) {
    refreshPromise = refreshSession().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.API_ENDPOINT,
  timeout: REQUEST_TIMEOUT_MS,
  prepareHeaders: (headers, { getState }) => {
    const auth = (getState() as RootState).auth;
    const token = auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (env.API_KEY) {
      headers.set('x-api-key', env.API_KEY);
    }
    const orgId = auth.user?.organizationId;
    if (orgId) {
      headers.set('x-organization-id', orgId);
    }
    return headers;
  },
});

async function applyRefresh(api: BaseQueryApi, force: boolean): Promise<boolean> {
  const state = api.getState() as RootState;
  if (!force && !needsProactiveRefresh(state.auth.token)) {
    return true;
  }

  const refreshResult = await runSharedRefresh();
  if (!refreshResult) {
    api.dispatch(logout());
    return false;
  }

  const existingOrgId = (api.getState() as RootState).auth.user?.organizationId;
  api.dispatch(setCredentials(credentialsFromRefresh(refreshResult, existingOrgId)));
  return true;
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await applyRefresh(api, false);

  let result = await rawBaseQuery(args, api, extraOptions);

  const token = (api.getState() as RootState).auth.token;
  if (result.error && shouldAttemptReauth(result.error, token)) {
    const ok = await applyRefresh(api, true);
    if (ok) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Agent',
    'Organisation',
    'Member',
    'Invitation',
    'Role',
    'Application',
    'Webhook',
    'DataSource',
    'ApiKeyProfile',
    'Analytics',
    'Profile',
    'Billing',
    'Invoice',
    'Session',
    'Preferences',
  ],
  endpoints: () => ({}),
});
